const _ = require('lib/underscore')
const Backbone = require('lib/backbone')
const Marionette = require('lib/marionette')
const BaseModel = require('lib/base_model')
const log = require('log').create('freeform', 'element.base') // eslint-disable-line

const default_options = {
    tel: {
        value: '',
        show_label_before: true,
    },
    text: {
        value: '',
        show_label_before: true,
    },
    email: {
        value: '',
        show_label_before: true,
    },
    file: {
        value: '',
        show_label_before: true,
    },
    number: {
        value: '',
        show_label_before: true,
    },
    search: {
        value: '',
        show_label_before: true,
    },
    url: {
        value: '',
        show_label_before: true,
    },
    hidden: {
        value: '',
        show_label_before: true,
    },
    password: {
        value: '',
        show_label_before: true,
    },
    textarea: {
        value: '',
        show_label_before: true,
    },
    checkbox: {
        value: '',
        show_label_after: true,
        checked: false,
    },
    radio: {
        show_label_after: true,
        checked: false,
    },
    radioset: {
        value: '',
        show_label_before: true,
    },
    select: {
        value: '',
        show_label_before: true,
    },
    buttonfield: {
        value: '',
        show_label_before: true,
    },
    submit: {
        label: 'Submit',
        show_label_before: false,
        show_label_after: false,
        show_error: false,
    },
    reset: {
        label: 'Reset',
        show_label_before: false,
        show_label_after: false,
        show_error: false,
    },
    button: {
        show_label_before: false,
        show_label_after: false,
        show_error: false,
    },
    buttonset: {
        show_label_before: false,
        show_label_after: false,
        show_error: false,
    },
}

const BaseElement = BaseModel.extend({
    defaults() {
        return {
            type: null,
            value: null,
            label: null,
            placeholder: null,
            name: this.cid,
            id: this.cid,
            disabled: false,
            related_key: null,
            related_model: null,
            validator: null,
            error: null,
            confirmation: null,
            display_errors: true,
            display_confirmations: true,
            display_note: true,

            // view options
            el: null,
            show_label: true,
            show_error: true,
            show_confirmation: true,
            show_note: true,
            show_label_before: null,
            show_label_after: null,
            label_class: 'label',
            error_class: 'error glyph-attention',
            confirmation_class: 'confirmation glyph-confirmation',
            note_class: 'note',
        }
    },

    defaultElementEvents: {
        'change:value': 'validateElement',
        'change:related_model': 'initializeRelatedModel',
        'change:related_key': 'initializeRelatedModel',
    },
    elementEvents: {
        // empty events hash for specific Element types to override
    },

    defaultCollectionEvents: {
        'change:related_model': 'initializeRelatedModel',
    },
    collectionEvents: {
        // empty events hash for specific Element types to override
    },

     
    constructor: function constructor(input_attrs, options) {
        const attrs = this.validateConstructor(input_attrs)

        const defaults = default_options[attrs.type]
        if (defaults) _.defaults(attrs, defaults)

        BaseModel.apply(this, [attrs, options])

        Marionette.bindEvents(this, this, this.defaultElementEvents)
        Marionette.bindEvents(this, this, this.elementEvents)

        if (this.collection) {
            Marionette.bindEvents(this, this.collection, this.defaultCollectionEvents)
            Marionette.bindEvents(this, this.collection, this.collectionEvents)
        }

        this.initializeRelatedModel()
        this.initializeElement()
    },

    validateConstructor(attrs) {
        // empty attribute validator
        // for specific Element types to override
        return attrs
    },
    validateRelatedModel(related_model) {
        if (! related_model) return
        if (! (related_model instanceof Backbone.Model)) {
            throw new Error('Related model must be a model.')
        }
    },

    initializeElement() {
        // empty setup method, called at end of construction
        // for specific Element types to override
    },

    initializeRelatedModel() {
        // check for a related model and related key
        const related = this.getRelated()
        if (! related) return

        this.validateRelatedModel(related.model)

        // stop listening to the previous related model
        if (this.related_model) {
            this.stopListening(this.related_model)
        }

        // assume the related model's value as our own
        // do it silently, so we don't trigger validation during initialization
        const value = related.model.get(related.key)
        this.set('value', value, { silent: true })

        // listen for value changes on the related model
        this.listenTo(related.model, `change:${related.key}`, this.onRelatedModelChange)

        // save a reference so we can stopListening if a new related_model is provided
        this.related_model = related.model
    },

    getRelated() {
        const related_model = this.get('related_model') || this.collection && this.collection.related_model
        const related_key = this.get('related_key')

        if (related_model && related_key) {
            return {
                model: related_model,
                key: related_key,
            }
        }

        return false
    },

    onRelatedModelChange(model, value, options) {
        this.set('value', value)
    },

    validateElement() {
        this.unset('confirmation')
        const is_valid = this.isValid()
        const error = this.validationError
        this.set('error', error)

        const related = this.getRelated()
        if (related && is_valid) {
            const value = this.get('value')
            related.model.set(related.key, value)
        }
    },

    validateRelated(value) {
        // validate against the related model, if defined
        const related = this.getRelated()
        if (related && related.model.validateAttribute) {
            const error = related.model.validateAttribute(related.key, value)
            if (error) return error
        }
    },

    validators: {
        'value'(value, attrs) {
            let error

            // validate with model validator, if defined
            // or, validate against related model, if defined
            const validator = this.get('validator')
            if (_.isFunction(validator)) {
                error = validator.call(this, value)
            } else {
                error = this.validateRelated(value)
            }

            return error
        },
    },

})

module.exports = BaseElement
